import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getPublicMenu, resolveMediaUrl } from '../lib/api';
import { toast } from 'sonner';
import { Minus, Plus, ShoppingBag, Clock } from 'lucide-react';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { displayBrandName } from '../lib/brand';
import { closedNoticeText, closedButtonLabel, isStoreClosed } from '../lib/storeHoursDisplay';
import { useStoreStatus } from '../lib/useStoreStatus';
import { formatLineLabel, reconcileCart, isPizzaItem } from '../lib/cartUtils';
import { buildCartLine, cartTotals, lineCartKey } from '../lib/pizzaPricing';
import { emitCartChange } from '../lib/cartEvents';
import { flyToCart, replayClass } from '../lib/motion';
import PizzaArt from '../components/pizzeria/PizzaArt';
import ProductSheet from '../components/pizzeria/ProductSheet';
import AnimatedPrice from '../components/pizzeria/AnimatedPrice';
import StatusPill from '../components/pizzeria/StatusPill';
import Reveal from '../components/pizzeria/Reveal';

/** Foto del producto con shimmer mientras carga (#13); sin foto → pizza ilustrada. */
function ItemImage({ item }) {
  const [state, setState] = useState('loading');
  const src = item.image_url ? resolveMediaUrl(item.image_url) : '';
  if (src && state !== 'error') {
    return (
      <div className={`relative w-full h-full ${state === 'loading' ? 't-shimmer' : ''}`}>
        <img
          src={src}
          alt={item.name}
          loading="lazy"
          className={`w-full h-full object-cover transition-opacity duration-300 ${state === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
        />
      </div>
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FFE7D6] to-[#FFF6EA]" role="img" aria-label={`Ilustración de pizza: ${item.name}`}>
      <PizzaArt className="h-[82%] w-auto drop-shadow-lg" />
    </div>
  );
}

/** Categorías con al menos un producto (las vacías no se muestran al cliente). */
function nonEmptyCategories(categories) {
  return (categories || []).filter((c) => (c.items || []).length > 0);
}

function priceRangeLabel(item) {
  if (!isPizzaItem(item) || !item.sizes?.length) {
    return `$${Number(item.price).toFixed(0)}`;
  }
  const prices = item.sizes.map((s) => Number(s.price) || 0);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `$${min.toFixed(0)}`;
  return `$${min.toFixed(0)} – $${max.toFixed(0)}`;
}

function cartTarget() {
  const bar = document.getElementById('t-cart-target-bar');
  if (bar && bar.getBoundingClientRect().width) return bar;
  return document.getElementById('t-cart-target-header');
}

export default function MenuOrderPage() {
  const { info, schedule } = useClinicBranding();
  const storeClosed = useStoreStatus();
  const [searchParams] = useSearchParams();
  const [menu, setMenu] = useState({ categories: [], topping_extras: [], store: null });
  const [cart, setCart] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [pickerItem, setPickerItem] = useState(null);
  const [originRect, setOriginRect] = useState(null);
  const [sizeKey, setSizeKey] = useState('mediana');
  const [halfId, setHalfId] = useState('');
  const [extraIds, setExtraIds] = useState([]);
  const sheetMediaRef = useRef(null);
  const barRef = useRef(null);
  const countRef = useRef(null);
  const tabsRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const allPizzas = useMemo(() => {
    const list = [];
    for (const cat of menu.categories || []) {
      for (const it of cat.items || []) {
        if (isPizzaItem(it)) list.push(it);
      }
    }
    return list;
  }, [menu.categories]);

  useEffect(() => {
    let saved = [];
    try {
      const raw = sessionStorage.getItem('pizas_cart');
      if (raw) saved = JSON.parse(raw);
    } catch {
      /* ignore */
    }
    getPublicMenu()
      .then((data) => {
        setMenu({ ...data, loaded: true });
        const { cart: synced, removed } = reconcileCart(saved, data.categories, data.topping_extras || []);
        if (removed.length) {
          toast.warning(`Se actualizó el menú. Quitamos del carrito: ${removed.join(', ')}.`, { duration: 8000 });
        }
        setCart(synced);
        const firstWithItems = nonEmptyCategories(data.categories)[0];
        if (firstWithItems) setActiveCat(firstWithItems.id);
      })
      .catch(() => {
        setMenu((prev) => ({ ...prev, loaded: true }));
        toast.error('No se pudo cargar el menú');
      });
  }, []);

  useEffect(() => {
    sessionStorage.setItem('pizas_cart', JSON.stringify(cart));
    emitCartChange();
  }, [cart]);

  const openPicker = useCallback((item, fromEl) => {
    if (!isPizzaItem(item)) return;
    setOriginRect(fromEl ? fromEl.getBoundingClientRect() : null);
    setPickerItem(item);
    setSizeKey(item.sizes?.[0]?.key || 'mediana');
    setHalfId('');
    setExtraIds([]);
  }, []);

  // /pedir?item=<id> (desde «Del menú» en Inicio) abre directamente la hoja del producto
  const itemParam = searchParams.get('item');
  useEffect(() => {
    if (!menu.loaded || !itemParam) return;
    for (const cat of menu.categories || []) {
      const it = (cat.items || []).find((x) => x.id === itemParam);
      if (it) {
        setActiveCat(cat.id);
        if (isPizzaItem(it)) openPicker(it, null);
        break;
      }
    }
  }, [menu.loaded, menu.categories, itemParam, openPicker]);

  const celebrateAdd = (fromEl) => {
    const target = cartTarget();
    flyToCart(fromEl, target).then(() => {
      replayClass(countRef.current, 't-bounce');
      replayClass(barRef.current, 't-breathe');
      const hdr = document.getElementById('t-cart-target-header');
      replayClass(hdr, 't-bounce');
    });
  };

  const addSimple = (item, fromEl) => {
    const line = buildCartLine({ item, qty: 1 });
    setCart((prev) => {
      const key = lineCartKey(line);
      const ix = prev.findIndex((x) => lineCartKey(x) === key);
      if (ix >= 0) {
        const next = [...prev];
        const qty = next[ix].qty + 1;
        next[ix] = { ...next[ix], qty, line_total: qty * next[ix].unit_price };
        return next;
      }
      return [...prev, line];
    });
    toast.success(`${item.name} añadida`);
    requestAnimationFrame(() => celebrateAdd(fromEl));
  };

  const closePicker = useCallback(() => setPickerItem(null), []);

  const confirmPizza = () => {
    if (!pickerItem) return;
    const half = halfId ? allPizzas.find((p) => p.id === halfId) : null;
    const extras = (menu.topping_extras || []).filter((t) => extraIds.includes(t.id));
    const line = buildCartLine({
      item: pickerItem,
      sizeKey,
      qty: 1,
      halfSpecialty: half,
      extraToppings: extras,
    });
    setCart((prev) => {
      const key = lineCartKey(line);
      const ix = prev.findIndex((x) => lineCartKey(x) === key);
      if (ix >= 0) {
        const next = [...prev];
        const qty = next[ix].qty + 1;
        next[ix] = { ...next[ix], qty, line_total: qty * next[ix].unit_price };
        return next;
      }
      return [...prev, line];
    });
    toast.success(`${pickerItem.name} añadida`);
    // Clon de la pizza antes de cerrar la hoja, para el vuelo al carrito
    const media = sheetMediaRef.current;
    let ghostSrc = null;
    if (media) {
      ghostSrc = media.cloneNode(true);
      const r = media.getBoundingClientRect();
      Object.assign(ghostSrc.style, {
        position: 'fixed', left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`,
        transform: 'none', opacity: 0, pointerEvents: 'none',
      });
      document.body.appendChild(ghostSrc);
    }
    setPickerItem(null);
    requestAnimationFrame(() => {
      celebrateAdd(ghostSrc);
      if (ghostSrc) setTimeout(() => ghostSrc.remove(), 50);
    });
  };

  const changeQty = (key, delta) => {
    setCart((prev) => {
      const ix = prev.findIndex((x) => lineCartKey(x) === key);
      if (ix < 0) return prev;
      const next = [...prev];
      const qty = next[ix].qty + delta;
      if (qty <= 0) return prev.filter((_, i) => i !== ix);
      next[ix] = { ...next[ix], qty, line_total: qty * next[ix].unit_price };
      return next;
    });
  };

  const toggleExtra = useCallback((id) => {
    setExtraIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const deliveryFee = Number(menu.store?.delivery_fee || 0);
  const totals = cartTotals(cart, deliveryFee);
  const itemCount = cart.reduce((s, c) => s + c.qty, 0);
  const categories = nonEmptyCategories(menu.categories);
  const visibleCat = categories.find((c) => c.id === activeCat) || categories[0];
  const closed = isStoreClosed(storeClosed);
  const closedNotice = closedNoticeText(storeClosed, schedule);
  const brand = displayBrandName(info?.name);
  const legal = menu.store?.legal_terms || {};

  // Indicador deslizante de la pestaña activa
  useLayoutEffect(() => {
    const wrap = tabsRef.current;
    if (!wrap || !visibleCat) return;
    const btn = wrap.querySelector(`[data-cat="${visibleCat.id}"]`);
    if (btn) {
      setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
      if (btn.scrollIntoView && wrap.scrollWidth > wrap.clientWidth) {
        btn.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
    }
  }, [visibleCat, categories.length]);

  const previewUnit = pickerItem
    ? buildCartLine({
        item: pickerItem,
        sizeKey,
        halfSpecialty: halfId ? allPizzas.find((p) => p.id === halfId) : null,
        extraToppings: (menu.topping_extras || []).filter((t) => extraIds.includes(t.id)),
      }).unit_price
    : 0;

  return (
    <div className="min-h-screen t-paper pb-36">
      <div className="max-w-6xl mx-auto px-4 pt-8 md:pt-12">
        <header className="text-center mb-6">
          <p className="font-hand text-2xl text-[#C2410C]">nuestro menú</p>
          <h1 className="font-heading text-5xl md:text-6xl font-extrabold text-[#2B1A12]">{brand}</h1>
          <p className="text-[#6B5444] mt-2 text-lg">Pide en línea — entrega a domicilio</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
            <span className="t-sticker t-sticker-pop t-wiggle text-lg" style={{ '--d': '.15s' }}>2x1 en pizzas</span>
            <span className="t-sticker t-sticker--tomato t-sticker-pop t-wiggle text-lg" style={{ '--d': '.3s', transform: 'rotate(4deg)' }}>
              Una sola pizza −30%
            </span>
          </div>
          {/* Paquete legal (doc. 05 §4, §6 y §8): condiciones, zona, tiempo y precios */}
          <p className="mt-3 text-sm text-[#6B5444]" data-testid="menu-promo-conditions">
            2x1 en pizzas · 30% en una sola pizza. Vigencia: {legal.vigencia_promos || 'hasta nuevo aviso'}. No
            acumulables entre sí ni con cupones.{' '}
            <Link to="/terminos#promociones" className="font-semibold underline underline-offset-2">
              Ver condiciones
            </Link>
          </p>
          <p className="mt-1 text-sm text-[#6B5444]">
            {legal.radio_entrega_km ? `Entregamos hasta ${legal.radio_entrega_km} km de la sucursal. ` : ''}
            {legal.tiempo_entrega ? `Tiempo estimado: ${legal.tiempo_entrega}. ` : ''}
            {legal.pedido_minimo ? `Pedido mínimo: ${legal.pedido_minimo}. ` : ''}
            Envío: {legal.costo_envio || (deliveryFee > 0 ? `$${deliveryFee.toFixed(2)} MXN` : '$0.00 MXN (gratis)')}. Precios
            en MXN, IVA incluido. Imágenes ilustrativas. Consulta ingredientes y alérgenos antes de pedir.
          </p>
          <div className="mt-5 flex flex-col items-center gap-2">
            <StatusPill status={storeClosed} schedule={schedule} />
            {closed && closedNotice ? (
              <p role="status" className="inline-flex items-center gap-2 text-base font-semibold text-[#2B1A12] bg-[#FFFDF8] border border-[#F1DFC8] rounded-2xl px-4 py-2">
                <Clock className="w-4 h-4 shrink-0 text-[#B8321F]" aria-hidden="true" />
                <span>{closedNotice} Puedes armar tu pedido desde ahora.</span>
              </p>
            ) : null}
          </div>
        </header>

        {/* Pestañas sticky con indicador deslizante */}
        {categories.length > 1 && (
          <div className="sticky top-[96px] z-30 -mx-4 px-4 py-3 bg-[#FFF6EA]/90 backdrop-blur-md mb-6">
            <div ref={tabsRef} className="t-tabs relative flex gap-1 overflow-x-auto snap-x md:justify-center" role="tablist" aria-label="Categorías del menú">
              <span
                className="t-tab-indicator absolute top-0 bottom-0 left-0 rounded-full bg-[#C2410C] shadow"
                style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }}
                aria-hidden="true"
              />
              {categories.map((cat) => {
                const active = visibleCat?.id === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-cat={cat.id}
                    className={`relative z-[1] snap-start shrink-0 px-5 min-h-[44px] rounded-full font-bold whitespace-nowrap transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#F2B33D] ${
                      active ? 'text-white' : 'text-[#2B1A12] hover:text-[#C2410C]'
                    }`}
                    onClick={() => setActiveCat(cat.id)}
                  >
                    {cat.name}
                    <span className={`ml-1.5 text-xs ${active ? 'text-white/85' : 'text-[#6B5444]'}`}>{(cat.items || []).length}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!menu.loaded ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true" aria-label="Cargando menú">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="t-card overflow-hidden">
                <div className="t-shimmer h-44" />
                <div className="p-5 space-y-3">
                  <div className="t-shimmer h-6 w-2/3 rounded" />
                  <div className="t-shimmer h-4 w-full rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {menu.loaded && categories.length === 0 ? (
          <div className="t-card text-center py-14 px-6 max-w-lg mx-auto">
            <PizzaArt className="w-28 h-28 mx-auto" />
            <p className="font-heading text-2xl font-bold mt-4">Estamos actualizando nuestro menú</p>
            <p className="mt-1 text-[#6B5444]">Vuelve en unos minutos o escríbenos por WhatsApp.</p>
          </div>
        ) : null}

        {visibleCat && (
          <section key={visibleCat.id} aria-labelledby={`cat-${visibleCat.id}`}>
            <h2 id={`cat-${visibleCat.id}`} className="font-heading text-3xl md:text-4xl font-extrabold mb-5">
              {visibleCat.name}
            </h2>
            <Reveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {(visibleCat.items || []).map((item, i) => {
                const pizza = isPizzaItem(item);
                return (
                  <article
                    key={item.id}
                    className="t-card t-reveal overflow-hidden flex sm:flex-col relative"
                    style={{ '--i': Math.min(i, 8) }}
                  >
                    <div className="t-card__media relative w-32 shrink-0 sm:w-full h-auto min-h-[128px] sm:h-48 overflow-hidden" data-media>
                      <ItemImage item={item} />
                      {item.is_named_specialty && (
                        <span className="t-sticker absolute top-2 left-2 text-xs sm:text-sm">Especial</span>
                      )}
                    </div>
                    <div className="p-4 sm:p-5 flex flex-col flex-1 min-w-0">
                      <h3 className="font-heading text-xl md:text-2xl font-bold leading-tight">
                        {pizza ? (
                          <button
                            type="button"
                            className="text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
                            onClick={(e) => openPicker(item, e.currentTarget.closest('article')?.querySelector('[data-media]'))}
                          >
                            {item.name}
                          </button>
                        ) : (
                          item.name
                        )}
                      </h3>
                      {item.description && <p className="text-sm text-[#6B5444] mt-1 line-clamp-2">{item.description}</p>}
                      <div className="flex items-center justify-between mt-auto pt-3 gap-2">
                        <span className="text-lg font-bold text-[#2B1A12] tabular">{priceRangeLabel(item)}</span>
                        <button
                          type="button"
                          className="relative z-[2] inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#C2410C] text-white shadow-md hover:bg-[#A8360A] active:scale-95 transition-transform focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-[#F2B33D]"
                          aria-label={pizza ? `Elegir opciones de ${item.name}` : `Añadir ${item.name} al carrito`}
                          onClick={(e) => {
                            const media = e.currentTarget.closest('article')?.querySelector('[data-media]');
                            if (pizza) openPicker(item, media);
                            else addSimple(item, media);
                          }}
                        >
                          <Plus className="w-6 h-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </Reveal>
          </section>
        )}
      </div>

      {pickerItem ? (
        <ProductSheet
          item={pickerItem}
          originRect={originRect}
          sizeKey={sizeKey}
          setSizeKey={setSizeKey}
          halfId={halfId}
          setHalfId={setHalfId}
          extraIds={extraIds}
          toggleExtra={toggleExtra}
          allPizzas={allPizzas}
          toppingExtras={menu.topping_extras || []}
          unitPrice={previewUnit}
          onConfirm={confirmPizza}
          onClose={closePicker}
          mediaRef={sheetMediaRef}
        />
      ) : null}

      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-3 t-slide-up" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div ref={barRef} className="max-w-5xl mx-auto rounded-[22px] bg-[#2B1A12] text-[#FFF6EA] shadow-2xl px-4 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-bold flex items-center gap-2 flex-wrap text-lg">
                  <span id="t-cart-target-bar" className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F2B33D] text-[#2B1A12]">
                    <ShoppingBag className="w-5 h-5" aria-hidden="true" />
                    <span
                      ref={countRef}
                      className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full bg-[#B8321F] text-white text-xs font-bold flex items-center justify-center tabular"
                      aria-hidden="true"
                    >
                      {itemCount}
                    </span>
                  </span>
                  <span>
                    {itemCount} artículo{itemCount !== 1 ? 's' : ''} ·{' '}
                    <AnimatedPrice value={Math.round(totals.total)} suffix=" MXN" />
                  </span>
                </p>
                {totals.auto_discount > 0 ? (
                  <p className="text-sm font-bold text-[#9BE08F] mt-0.5" key={totals.auto_discount}>
                    <span className="t-pulse-text inline-block">
                      Ahorraste <AnimatedPrice value={Math.round(totals.auto_discount)} live={false} /> · {totals.auto_discount_detail}
                    </span>
                  </p>
                ) : null}
                <ul className="text-sm text-[#FFF6EA]/90 mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
                  {cart.map((c) => {
                    const key = lineCartKey(c);
                    return (
                      <li key={key} className="flex items-center gap-1.5">
                        {formatLineLabel(c)}
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/30 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F2B33D]"
                          onClick={() => changeQty(key, -1)}
                          aria-label={`Quitar una unidad de ${formatLineLabel(c)}`}
                        >
                          <Minus className="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                        <span className="min-w-[1.25rem] text-center font-bold tabular" aria-live="polite">
                          {c.qty}
                        </span>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/30 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F2B33D]"
                          onClick={() => changeQty(key, 1)}
                          aria-label={`Agregar una unidad de ${formatLineLabel(c)}`}
                        >
                          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {closed ? (
                <button
                  type="button"
                  className="t-btn shrink-0 bg-[#FFF6EA] text-[#2B1A12] opacity-100 cursor-not-allowed"
                  disabled
                  title={closedNotice}
                >
                  {closedButtonLabel(storeClosed, schedule)}
                </button>
              ) : (
                <Link to="/checkout" className="t-btn t-btn-primary shrink-0 text-lg">
                  Ir a pagar
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
